import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { Group, Textarea, FileInput, Stack, ActionIcon, Tooltip } from "@mantine/core";
import { useIntl } from "react-intl";
import { IconPaperclip, IconSend, IconSparkles } from "@tabler/icons-react";
import { useStyleEditor } from "../../components/StyleEditorContext.js";
import { useSelector } from "react-redux";
import { getCurrentSpec } from "../../utils/selectors.js";
import { Event } from "../../constants/events.js";
import { ChatAttachment } from "./ChatAttachment.js";
import { getChatId } from "../../reducers/chatReducer.js";
import { getInitStyle } from "../../reducers/initReducer.js";
import { useChatStore } from "../../hooks/useChatStore.js";
import { useTRPC } from "../../trpc.js";
import { useChatChangeStore } from "../../hooks/useChatChangeStore.js";
import { useMutation } from "@tanstack/react-query";
import { createMessageAttachment } from "../../utils/attachments.js";
import { isEqual } from "es-toolkit";
import { isEmpty } from "es-toolkit/compat";
const hiddenFormStyle = {
    display: "none",
};
function useListState(initialValue) {
    const [value, setValue] = useState(initialValue);
    const append = useCallback((item) => {
        setValue(prev => [...prev, item]);
    }, []);
    const remove = useCallback((id) => {
        setValue(prev => prev.filter(i => i.id !== id));
    }, []);
    const replace = useCallback((id, item) => {
        setValue(prev => prev.map(i => i.id === id ? item : i));
    }, []);
    const clear = useCallback(() => {
        setValue([]);
    }, []);
    const ref = useRef([]);
    ref.current = value;
    return [value, { append, remove, replace, clear }, ref];
}
const wrapperStyle = {
    border: "2px solid transparent",
    backgroundColor: "light-dark(white, black)",
    backgroundImage: "linear-gradient(light-dark(white, black), light-dark(white, black)), "
        + "linear-gradient(135deg, #ffc857 0%, #e066ff 50%, #4eb4d4 100%)",
    backgroundOrigin: "border-box",
    backgroundClip: "padding-box, border-box",
    borderRadius: 12,
    padding: "8px",
};
const textAreaStyles = {
    input: {
        border: "none",
        background: "inherit",
        resize: "none",
    },
    section: {
        alignItems: "flex-start",
        paddingTop: "calc(var(--input-padding-y) + 2px)",
    },
};
const Attachments = ({ attachments, onDelete }) => {
    return (_jsx(Group, { gap: "xs", children: attachments.map(a => _jsx(ChatAttachment, { attachment: a, onDelete: onDelete }, a.id)) }));
};
const useWithChatId = () => {
    const style = useSelector(getInitStyle);
    const intl = useIntl();
    const { newChat } = useChatChangeStore(style.id);
    const currentChatId = useSelector(getChatId);
    const chatIdRef = useRef(currentChatId);
    chatIdRef.current = currentChatId;
    const newChatMessage = intl.formatMessage({
        defaultMessage: "New chat",
        id: "label.newChat",
    });
    return useCallback((callback) => {
        if (chatIdRef.current) {
            callback(chatIdRef.current);
        }
        else {
            void newChat({
                summary: newChatMessage,
            }).then(({ id }) => {
                callback(id);
            });
        }
    }, [chatIdRef, newChat]);
};
export const ChatInput = ({ inputRef, onSendMessage }) => {
    const intl = useIntl();
    const { on, off } = useStyleEditor();
    const fileButtonRef = useRef(null);
    const fileInputFormRef = useRef(null);
    const currentSpec = useSelector(getCurrentSpec);
    const lastSentSpecRef = useRef(null);
    const [message, setCurrentMessage] = useState("");
    const style = useSelector(getInitStyle);
    const chatId = useSelector(getChatId);
    useEffect(() => {
        lastSentSpecRef.current = null;
    }, [chatId]);
    const { disabled, captureEvent, } = useStyleEditor();
    const { sendMessage, status } = useChatStore(style.id, chatId);
    const sending = status === "pending";
    const trpc = useTRPC();
    const { mutate: deleteAttachment } = useMutation(trpc.deleteMessageAttachment.mutationOptions());
    const [attachments, { remove: removeAttachment, append: appendAttachment, replace: replaceAttachment, clear: clearAttachments, }, attachmentsRef,] = useListState([]);
    const withChatId = useWithChatId();
    const clearFileInput = useCallback(() => {
        fileInputFormRef.current?.reset();
    }, [fileInputFormRef]);
    const handleSendMessage = useCallback(() => {
        if (!message.trim())
            return;
        if (sending)
            return;
        withChatId((chatIdForMessage) => {
            const messageAttachments = attachments.filter(a => "type" in a);
            const chatMessage = {
                role: "user",
                id: 0,
                chatId: chatIdForMessage,
                content: [{
                        type: "text",
                        text: message,
                    }],
            };
            if (!isEqual(currentSpec, lastSentSpecRef.current)) {
                chatMessage.spec = currentSpec;
                lastSentSpecRef.current = currentSpec;
            }
            if (!isEmpty(messageAttachments)) {
                chatMessage.attachments = messageAttachments;
            }
            captureEvent(Event.SendChatMessage, {
                numberOfAttachments: messageAttachments.length,
                hasSpec: Boolean(chatMessage.spec),
                message: message.slice(0, 200),
            });
            sendMessage(chatIdForMessage, chatMessage);
            onSendMessage?.(chatIdForMessage);
            setCurrentMessage("");
            clearAttachments();
            clearFileInput();
        });
    }, [
        message,
        attachments,
        currentSpec,
        sending,
        withChatId,
        captureEvent,
        sendMessage,
    ]);
    const handleOnChange = useCallback((event) => {
        setCurrentMessage(event.currentTarget.value);
    }, []);
    const handleEnter = useCallback((event) => {
        event.stopPropagation();
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            handleSendMessage();
        }
        if (event.key === "Escape") {
            setCurrentMessage("");
        }
    }, [handleSendMessage]);
    const handleAttach = useCallback(() => {
        fileButtonRef.current?.click();
    }, [fileButtonRef.current]);
    const handleDeleteAttachment = useCallback((id) => {
        if (typeof id === "number") {
            deleteAttachment(id);
            removeAttachment(id);
        }
        if (typeof id === "string") {
            removeAttachment(id);
        }
    }, [deleteAttachment]);
    useEffect(() => {
        const clear = () => {
            clearFileInput();
            attachmentsRef.current.forEach(({ id }) => {
                if (typeof id === "number") {
                    deleteAttachment(id);
                    removeAttachment(id);
                }
                if (typeof id === "string") {
                    removeAttachment(id);
                }
            });
        };
        on("newChat", clear);
        return () => {
            off("newChat", clear);
        };
    }, [on, off, deleteAttachment, removeAttachment]);
    const handleFileChange = useCallback((files = []) => {
        withChatId((chatIdForUpload) => {
            files.forEach((file) => {
                captureEvent(Event.AddChatAttachment);
                const id = crypto.randomUUID();
                appendAttachment({
                    id,
                    file,
                    chatId: chatIdForUpload,
                });
                void createMessageAttachment({
                    id,
                    file,
                    chatId: chatIdForUpload,
                }).then((attachment) => {
                    replaceAttachment(id, attachment);
                }).catch(() => {
                    removeAttachment(id);
                });
            });
            clearFileInput();
        });
    }, [withChatId, captureEvent, appendAttachment]);
    const pendingAttachments = attachments.some(a => "file" in a);
    return (_jsxs(Stack, { style: wrapperStyle, children: [_jsxs(Stack, { children: [attachments.length > 0 && (_jsx(Attachments, { attachments: attachments, onDelete: handleDeleteAttachment })), _jsx(Textarea, { ref: inputRef, id: "ai-chat-input", autoFocus: true, autosize: true, flex: 1, disabled: disabled, onChange: handleOnChange, onKeyDown: handleEnter, styles: textAreaStyles, value: message, minRows: 2, maxRows: 10, bg: "inherit", leftSection: _jsx(IconSparkles, { size: 20, color: "light-dark(black, white)" }), leftSectionPointerEvents: "none", placeholder: intl.formatMessage({
                            defaultMessage: "Describe how you want the map to look ...",
                            id: "styleEditor.aiInputPlaceholder",
                        }) })] }), _jsxs(Group, { justify: "space-between", bg: "inherit", children: [_jsx("form", { ref: fileInputFormRef, style: hiddenFormStyle, children: _jsx(FileInput, { id: "file-input", ref: fileButtonRef, multiple: true, onChange: handleFileChange }) }), _jsx(Tooltip, { label: intl.formatMessage({ defaultMessage: "Attach file", id: "button.attachFile" }), children: _jsx(ActionIcon, { variant: "subtle", color: "black", size: "lg", loading: pendingAttachments, onClick: handleAttach, children: _jsx(IconPaperclip, { size: 18 }) }) }), _jsx(Tooltip, { label: intl.formatMessage({ defaultMessage: "Send message", id: "button.sendMessage" }), children: _jsx(ActionIcon, { ml: "auto", variant: "filled", color: "black", size: "lg", radius: 6, loading: sending || pendingAttachments, onClick: handleSendMessage, children: _jsx(IconSend, { size: 18 }) }) })] })] }));
};
